import logger from '@/app/logger';
import mitt from 'mitt';
import type { Handler } from 'mitt';
import type { ComponentPublicInstance } from 'vue';

const emitter = mitt();

// Listeners should be component specific, which allows unsubscribing for one component only
const componentListeners = new Map<ComponentPublicInstance, { event: string; handler: Handler<any> }[]>();

export interface EventBus {
  $on(event: string, handler: Handler<any>, listenerScope?: ComponentPublicInstance | null): void;
  $off(event: string, component: ComponentPublicInstance | null): void;
  $emit(event: string, arg?: any): void;
}

/**
 * EventBus is a simple event bus implementation using mitt. Consider refactoring this and use
 * Vue's built-in event system. Global events should be avoided if possible as they increase complexity.
 */
export default {
  $on(event: string, handler: Handler<any>, listenerScope?: ComponentPublicInstance | null) {
    emitter.on(event, handler);

    if (listenerScope) {
      if (!componentListeners.has(listenerScope)) {
        componentListeners.set(listenerScope, []);
      }
      componentListeners.get(listenerScope)?.push({ event, handler });
    } else {
      logger.warn(
        `EventBus on: ${event} - No component provided, thus unsubscribing fill impact all subscriptions regardless of component`
      );
    }
  },
  $off(event: string, listenerScope?: ComponentPublicInstance | null) {
    if (listenerScope) {
      const listeners = componentListeners.get(listenerScope) || [];
      listeners.forEach(({ event: registeredEvent, handler }) => {
        if (registeredEvent === event) {
          emitter.off(event, handler);
        }
      });

      componentListeners.set(
        listenerScope,
        listeners.filter(({ event: registeredEvent }) => registeredEvent !== event)
      );
    } else {
      logger.warn(`EventBus off: ${event} - No component provided, removing ${event} listeners for all components`);
      emitter.off(event);
    }
  },
  $emit(event: string, arg?: any) {
    emitter.emit(event, arg);
  },
} satisfies EventBus;
